import { useState } from "react";
import { toast } from "react-toastify";

// formik components
import { Formik, Form } from "formik";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import initialValues from "./schemas/initialValues";
import { askValidations, signInValidations } from "./schemas/validations";
import form from "./schemas/form";
import FormField from "./components/FormField";

import { getQueryErrorMessage } from "api/utils/queryError";
import { useUserContext } from "contexts/contextUser";

type AuthView = "signin" | "ask";

function Basic(): JSX.Element {
  const { formId, formField } = form;
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<AuthView>("signin");
  const isAsk = view === "ask";

  const { login, signup } = useUserContext();

  const handleSubmit = async (
    values: typeof initialValues,
    actions: { setSubmitting: (v: boolean) => void }
  ) => {
    try {
      if (isAsk) {
        await signup({
          email: values.email,
          password: values.password,
          firstname: values.firstname,
          lastname: values.lastname,
        });
        toast.success(
          "Request submitted. An administrator must approve your account before you can sign in."
        );
        setView("signin");
      } else {
        await login({ email: values.email, password: values.password });
        toast.success("Login successful!");
      }
    } catch (error) {
      if (isAsk) {
        toast.error(getQueryErrorMessage(error));
      } else {
        console.error("Login failed:", error);
        toast.error("Login failed. Please check your credentials.");
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ maxWidth: 450, mx: "auto", my: 10, py: 3, px: 2 }}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          textAlign="center"
          pb={1}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {isAsk ? "Request an account" : "Sign in"}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {isAsk && (
            <MDBox mb={2}>
              <MDTypography variant="h6" color="text" textAlign="center">
                Fill in your details. An administrator will review your request before you can sign
                in.
              </MDTypography>
            </MDBox>
          )}
          <Formik
            key={view}
            initialValues={initialValues}
            validationSchema={isAsk ? askValidations : signInValidations}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, isSubmitting }) => {
              const submitLabel = isAsk
                ? isSubmitting
                  ? "Submitting..."
                  : "Submit request"
                : isSubmitting
                ? "Signing in..."
                : "Sign in";

              return (
                <Form id={formId} autoComplete="off">
                  {isAsk && (
                    <>
                      <MDBox mb={2}>
                        <MDTypography variant="h6">First name :</MDTypography>
                        <FormField
                          name={formField.firstname.name}
                          label={formField.firstname.label}
                          type="text"
                          value={values.firstname}
                          placeholder={formField.firstname.placeholder}
                          error={errors.firstname && touched.firstname}
                          success={values.firstname.length > 0 && !errors.firstname}
                          fullWidth
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDTypography variant="h6">Last name :</MDTypography>
                        <FormField
                          name={formField.lastname.name}
                          label={formField.lastname.label}
                          type="text"
                          value={values.lastname}
                          placeholder={formField.lastname.placeholder}
                          error={errors.lastname && touched.lastname}
                          success={values.lastname.length > 0 && !errors.lastname}
                          fullWidth
                        />
                      </MDBox>
                    </>
                  )}
                  <MDBox mb={2}>
                    <MDTypography variant="h6">Email :</MDTypography>
                    <FormField
                      name={formField.email.name}
                      label={formField.email.label}
                      type="email"
                      value={values.email}
                      placeholder={formField.email.placeholder}
                      error={errors.email && touched.email}
                      success={values.email.length > 0 && !errors.email}
                      fullWidth
                    />
                  </MDBox>
                  <MDBox mb={2}>
                    <MDBox display="flex" alignItems="center">
                      <MDTypography variant="h6">Password :</MDTypography>
                      <Switch checked={visible} onChange={() => setVisible(!visible)} />
                      <MDTypography
                        variant="caption"
                        color="text"
                        onClick={() => setVisible(!visible)}
                        sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                      >
                        &nbsp;&nbsp; Show/Hide Password
                      </MDTypography>
                    </MDBox>
                    <FormField
                      name={formField.password.name}
                      label={formField.password.label}
                      type={visible ? "text" : "password"}
                      value={values.password}
                      placeholder={formField.password.placeholder}
                      error={errors.password && touched.password}
                      success={values.password.length > 0 && !errors.password}
                      fullWidth
                    />
                  </MDBox>
                  {isAsk && (
                    <MDBox mb={2}>
                      <MDTypography variant="h6">Repeat password :</MDTypography>
                      <FormField
                        name={formField.repeatPassword.name}
                        label={formField.repeatPassword.label}
                        type={visible ? "text" : "password"}
                        value={values.repeatPassword}
                        placeholder={formField.repeatPassword.placeholder}
                        error={errors.repeatPassword && touched.repeatPassword}
                        success={values.repeatPassword.length > 0 && !errors.repeatPassword}
                        fullWidth
                      />
                    </MDBox>
                  )}
                  <MDBox mt={4} mb={1}>
                    <MDButton
                      type="submit"
                      disabled={
                        isSubmitting ||
                        Object.keys(errors).length > 0 ||
                        Object.keys(touched).length === 0
                      }
                      variant="gradient"
                      color="info"
                      fullWidth
                    >
                      {submitLabel}
                    </MDButton>
                  </MDBox>
                  <MDBox mt={2} textAlign="center">
                    <MDButton
                      type="button"
                      variant="text"
                      color="info"
                      onClick={() => {
                        setVisible(false);
                        setView(isAsk ? "signin" : "ask");
                      }}
                    >
                      {isAsk
                        ? "Already have an account? Sign in"
                        : "Don't have an account? Request access"}
                    </MDButton>
                  </MDBox>
                </Form>
              );
            }}
          </Formik>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
}

export default Basic;
